import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import { imageProps } from "components/ImageIcon/ImageIcon";
import { ProvisioningStateDot } from "components/ProvisioningStateDot/ProvisioningStateDot";
import RecordLabel from "components/RecordLabel";
import { propagateScrollToSidebar } from "containers/Projects/components/ProjectTree/ProjectTree";
import { getTooltipOverlay } from "containers/Projects/components/ProjectTree/utils";
import { useState } from "react";
import { MenuItem } from "react-pro-sidebar";
import { Dispatch } from "redux";
import { getRelativeUrl, getTypedLowerCase, prefersIdInUrl } from "utils/service";
import "./ProjectTree.scss";
import { ProjectQuickAccessEntry } from "./interfaces";

interface CloseButtonProps {
    onClick: () => Promise<void>;
    actionIconClassName: string;
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
    additionalAction?: () => void;
}

const CloseButton = ({
    onClick,
    actionIconClassName,
    onMouseEnter,
    onMouseLeave,
    additionalAction,
}: CloseButtonProps) => {
    const [isDisabled, setDisabled] = useState(false);

    const handleClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setDisabled(true);
        try {
            await onClick();
        } finally {
            setDisabled(false);
            additionalAction?.();
        }
    };

    return (
        <IconButton
            icon={IconCollection.closeCircleOutline}
            onClick={handleClick}
            className={actionIconClassName}
            disabled={isDisabled}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        />
    );
};

interface CloseActionButtonProps {
    item: ProjectQuickAccessEntry;
    action: (item: ProjectQuickAccessEntry) => Promise<any>;
    actionIconClassName: string;
}

export const CloseActionButton = ({
    item,
    action,
    actionIconClassName,
}: CloseActionButtonProps) => {
    const handleClick = async () => {
        await action(item);
    };

    return <CloseButton onClick={handleClick} actionIconClassName={actionIconClassName} />;
};

interface CloseAllActionButtonProps {
    itemList: ProjectQuickAccessEntry[];
    action: (item: ProjectQuickAccessEntry) => Promise<any>;
    actionIconClassName: string;
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
    enableSingleActions: () => void;
    disableSingleActions: () => void;
}

export const CloseAllActionButton = ({
    itemList,
    action,
    actionIconClassName,
    onMouseEnter,
    onMouseLeave,
    enableSingleActions,
    disableSingleActions,
}: CloseAllActionButtonProps) => {
    const handleClick = async () => {
        disableSingleActions();
        await Promise.allSettled(itemList.map(action));
    };

    return (
        <CloseButton
            onClick={handleClick}
            actionIconClassName={actionIconClassName}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            additionalAction={enableSingleActions}
        />
    );
};
interface CreateMenuChildrenProps {
    items?: ProjectQuickAccessEntry[];
    action: (item: ProjectQuickAccessEntry) => Promise<any>;
    actionIconClassName: string;
    itemClassName?: string;
    dispatch?: Dispatch<any>;
}

export const createMenuChildren = ({
    items,
    action,
    actionIconClassName,
    itemClassName,
    dispatch,
}: CreateMenuChildrenProps) =>
    items
        ? items.map((item) => {
              const innerContent = <CloseActionButton {...{ action, item, actionIconClassName }} />;
              const id = item.record_id_record.id;
              const recordName = item.record_id_record.name;
              const recordType = getTypedLowerCase(item.record_id_record.record_type);
              const recordLink = prefersIdInUrl(recordType)
                  ? getRelativeUrl(recordType, id, "id")
                  : getRelativeUrl(recordType, recordName, "name");
              const commonProps = {
                  name: recordName,
                  tooltipOverlayClassName: propagateScrollToSidebar,
                  tooltipOverlay: getTooltipOverlay({
                      name: recordName,
                      innerContent,
                      link: recordLink,
                  }),
              };
              return (
                  <MenuItem key={id} className={itemClassName}>
                      <Icon icon={IconCollection.pinOutline} className="pinnedIcon" />
                      {id ? <ProvisioningStateDot id={id} /> : null}
                      <RecordLabel
                            link={recordLink}
                            iconByType={recordType}
                            image={item.record_id_record.resolved_icon}
                            {...imageProps(recordType, id)}
                            scrollIntoView
                            {...commonProps}
                        >
                            {innerContent}
                        </RecordLabel>
                  </MenuItem>
              );
          })
        : null;
